import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Zap, Shield, Heart, Brain, ArrowRight, Leaf, Droplets, Sun, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/SEO";

const benefits = [
  { icon: Zap, title: "40x More Nutrients", description: "Microgreens contain up to 40 times more vitamins and minerals than their mature counterparts." },
  { icon: Shield, title: "Powerful Antioxidants", description: "Rich in polyphenols and antioxidants that protect cells and reduce inflammation." },
  { icon: Heart, title: "Heart Health", description: "Studies show microgreens can help lower cholesterol and improve cardiovascular health." },
  { icon: Brain, title: "Brain Function", description: "High in vitamins and minerals that support cognitive function and mental clarity." }
];

const growthStages = [
  { stage: "Seed", day: "Day 0", description: "Premium organic seeds are selected and soaked to begin germination." },
  { stage: "Germination", day: "Day 1-3", description: "Seeds sprout in controlled conditions with optimal moisture and temperature." },
  { stage: "Growth", day: "Day 4-7", description: "Seedlings develop their first true leaves under natural light." },
  { stage: "Harvest", day: "Day 7-14", description: "Microgreens are harvested at peak nutrition, just above the soil line." }
];

const comparison = [
  { feature: "Growing Medium", microgreens: "Soil or growing mat", sprouts: "Water only" },
  { feature: "Light Required", microgreens: "Yes", sprouts: "No" },
  { feature: "Harvest Part", microgreens: "Stem and leaves only", sprouts: "Entire plant including root" },
  { feature: "Growing Time", microgreens: "7-14 days", sprouts: "2-5 days" },
  { feature: "Nutrition", microgreens: "Very high (concentrated)", sprouts: "Moderate" },
  { feature: "Food Safety", microgreens: "Lower risk (no root)", sprouts: "Higher risk (moist conditions)" }
];

export default function AboutMicrogreensPage() {
  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="about-microgreens-page">
      <SEO title="About Microgreens" description="Discover the science behind microgreens - young seedlings packed with 4-40x more nutrients than mature plants. Health benefits, types, and how to use them." keywords="what are microgreens, microgreens benefits, microgreens nutrition" path="/about-microgreens" />
      {/* Compact Hero */}
      <section className="py-10 md:py-14 bg-praroha-forest" data-testid="microgreens-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-block px-4 py-1.5 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-4">
                Learn About Microgreens
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                What Are
                <span className="block text-praroha-sage">Microgreens?</span>
              </h1>
              <p className="text-white/60 text-sm leading-relaxed">
                Microgreens are young vegetable greens that are approximately 1-3 inches tall, harvested 
                7-14 days after germination. Unlike sprouts, only the stem and leaves are consumed, 
                making them safer and more flavorful.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_0c320f8b-e86f-46ef-9636-dfcf52e2f03a/artifacts/v5r3hcwh_freepik__create-an-ultrarealistic-macro-photograph-of-a-sin__11027.png"
                alt="Microgreen seedling sprouting from soil"
                className="rounded-2xl shadow-lg w-full aspect-[4/3] object-cover"
                data-testid="microgreens-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 md:py-12 bg-background" data-testid="superfoods-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-praroha-terracotta/10 text-praroha-terracotta text-sm font-medium rounded-full mb-4">
              Nutritional Benefits
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
              Why Microgreens Are Superfoods
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <Card className="group h-full bg-white hover:shadow-hover transition-all duration-300 border-border/50 hover:border-praroha-sage/30">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-4 group-hover:bg-praroha-forest group-hover:scale-110 transition-all duration-300">
                      <benefit.icon className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-praroha-forest mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Growth Process */}
      <section className="py-10 md:py-12 bg-praroha-forest text-white" data-testid="growth-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-full mb-4">
              The Growth Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold">
              How Microgreens Are Grown
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {growthStages.map((stage, index) => (
              <motion.div
                key={stage.stage}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="bg-white/10 border border-white/10 rounded-2xl p-6 h-full">
                  <div className="w-10 h-10 rounded-full bg-praroha-terracotta flex items-center justify-center mb-3">
                    <span className="font-heading text-sm font-bold">{index + 1}</span>
                  </div>
                  <p className="text-praroha-terracotta text-xs font-medium mb-1">{stage.day}</p>
                  <h3 className="font-heading text-lg font-semibold mb-2">{stage.stage}</h3>
                  <p className="text-white/60 text-sm">{stage.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-10 md:py-12 bg-background" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4">
              Know the Difference
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
              Microgreens vs Sprouts
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden border-border/50">
              <div className="grid grid-cols-3 bg-praroha-forest text-white">
                <div className="p-4 text-sm font-medium">Feature</div>
                <div className="p-4 text-sm font-medium text-center border-x border-white/20">Microgreens</div>
                <div className="p-4 text-sm font-medium text-center">Sprouts</div>
              </div>
              {comparison.map((row, index) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-praroha-cream/30' : 'bg-white'}`}
                >
                  <div className="p-3 text-sm font-medium text-praroha-forest">{row.feature}</div>
                  <div className="p-3 text-sm text-center text-muted-foreground border-x border-border/30">{row.microgreens}</div>
                  <div className="p-3 text-sm text-center text-muted-foreground">{row.sprouts}</div>
                </div>
              ))}
            </Card>
          </div>
        </div>
      </section>

      {/* Nutrient Facts */}
      <section className="py-10 md:py-12 bg-gradient-to-b from-background to-praroha-cream/30" data-testid="infographic-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-praroha-terracotta/10 text-praroha-terracotta text-sm font-medium rounded-full mb-4">
              Nutrition Facts
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
              Nutrient Concentration
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { value: "40x", label: "Vitamin C", desc: "Red cabbage microgreens have 40x more Vitamin C than mature red cabbage", color: "bg-praroha-forest/10", textColor: "text-praroha-forest" },
              { value: "6x", label: "Vitamin E", desc: "Cilantro microgreens contain 6x more Vitamin E than mature cilantro leaves", color: "bg-praroha-terracotta/10", textColor: "text-praroha-terracotta" },
              { value: "4x", label: "Beta-Carotene", desc: "Most microgreens have 4x more beta-carotene than their mature versions", color: "bg-praroha-sage/10", textColor: "text-praroha-sage" }
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-border/50 hover:shadow-hover transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto rounded-2xl ${item.color} flex items-center justify-center mb-4`}>
                      <span className={`font-heading text-2xl font-bold ${item.textColor}`}>{item.value}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-praroha-forest mb-2">{item.label}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-praroha-forest" data-testid="microgreens-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3">
            Ready to Experience the Difference?
          </h2>
          <p className="text-white/60 text-sm mb-6">
            Shop our selection of fresh, nutrient-packed microgreens and transform your meals.
          </p>
          <Link to="/products" data-testid="shop-microgreens-cta">
            <Button className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-8 py-5">
              Shop Microgreens
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
